import { queryKeys } from "@/lib/query-keys";
import bookingService, { Booking } from "@/lib/services/bookings";
import {
    QueryClient,
    useMutation,
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from "@tanstack/react-query";

// ── List ──────────────────────────────────────────────────────────────────────

export function useBookings(
    params?: { status?: string; service_type?: string; page?: number },
    options?: Omit<UseQueryOptions<{ data: Booking[]; meta: any }>, "queryKey" | "queryFn">
) {
    return useQuery({
        queryKey: queryKeys.bookings.list(params),
        queryFn: () => bookingService.getBookings(params),
        ...options,
    });
}

// ── Detail ────────────────────────────────────────────────────────────────────

export function useBooking(
    id: number,
    options?: Omit<UseQueryOptions<Booking>, "queryKey" | "queryFn">
) {
    return useQuery({
        queryKey: queryKeys.bookings.detail(id),
        queryFn: () => bookingService.getBooking(id),
        enabled: Number.isInteger(id) && id > 0,
        ...options,
    });
}

// ── Track ─────────────────────────────────────────────────────────────────────

export function useBookingTrack(
    id: number,
    options?: Omit<UseQueryOptions<{ booking: Booking }>, "queryKey" | "queryFn">
) {
    return useQuery({
        queryKey: queryKeys.bookings.track(id),
        queryFn: () => bookingService.getBookingTrack(id),
        enabled: Number.isInteger(id) && id > 0,
        ...options,
    });
}

// ── Invalidation ──────────────────────────────────────────────────────────────

export function invalidateBookingQueries(qc: QueryClient, bookingId: number): void {
    qc.invalidateQueries({ queryKey: queryKeys.bookings.byBooking(bookingId) });
}

export function syncBookingFromWebSocket(qc: QueryClient, booking: Booking): void {
    qc.setQueryData(queryKeys.bookings.detail(booking.id), booking);
    qc.setQueryData(queryKeys.bookings.track(booking.id), { booking });
}

export function syncBookingFromInspection(
  qc: QueryClient,
  booking: Booking,
  inspectionType: "pickup" | "delivery",
): void {
    const status =
        inspectionType === "pickup" ? "pickup_inspection" : "delivery_inspection";
    const updated = { ...booking, status };
    qc.setQueryData(queryKeys.bookings.detail(booking.id), updated);
    qc.setQueryData(queryKeys.bookings.track(booking.id), { booking: updated });
}

export function syncBookingFromAcceptance(
  qc: QueryClient,
  booking: Booking,
): void {
  const updated = { ...booking, status: "accepted" };
  qc.setQueryData(queryKeys.bookings.detail(booking.id), updated);
  qc.setQueryData(queryKeys.bookings.track(booking.id), { booking: updated });
} 


// ── Mutations ─────────────────────────────────────────────────────────────────

export function useCancelBooking() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, reason }: { id: number; reason: string }) =>
            bookingService.cancelBooking(id, reason),
        onSuccess: (updated) => {
            qc.invalidateQueries({ queryKey: queryKeys.bookings.byBooking(updated.id) });
        },
    });
}
